import { includesEnumValue, ProductTypeEnum, type Security, useSecurity } from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';

interface UseAllowMultipleAcctsPerCredParams {
  ulEnabled: boolean;
  symbol: string | undefined;
}

const ALLOWED_PRODUCT_TYPES = [ProductTypeEnum.CFD, ProductTypeEnum.PerpetualSwap];

/** Helper hook to determine if the allowMultipleAcctsPerCred option should be enabled in the useMarketSelector hook usage */
export const useAllowMultipleAcctsPerCred = ({ symbol, ulEnabled }: UseAllowMultipleAcctsPerCredParams) => {
  const { disableUnifiedLiquidityDerivativesMarketSelectionRestriction } = useFeatureFlag();
  const security = useSecurity(symbol);

  return useMemo(
    () =>
      allowMultipleAcctsPerCred({ disableUnifiedLiquidityDerivativesMarketSelectionRestriction, ulEnabled, security }),
    [disableUnifiedLiquidityDerivativesMarketSelectionRestriction, ulEnabled, security]
  );
};

/** Function to house the logic of when the user should be able to select multiple accounts per credential within a market in the market selector */
export function allowMultipleAcctsPerCred({
  disableUnifiedLiquidityDerivativesMarketSelectionRestriction,
  security,
  ulEnabled,
}: {
  ulEnabled: boolean;
  security: Security | undefined;
  disableUnifiedLiquidityDerivativesMarketSelectionRestriction: boolean;
}): boolean {
  if (!security) {
    return false;
  }

  return (
    disableUnifiedLiquidityDerivativesMarketSelectionRestriction &&
    ulEnabled &&
    includesEnumValue(ALLOWED_PRODUCT_TYPES, security.ProductType)
  );
}
